import React from "react";
import userTypeConsts from "../../../../../constants/userTypeConsts.js";
import { TagIconSVG } from "../trackerComponents/SvgIcons";
import { pointerCursorStyle, commonTextStyle } from "./constants";
import Plus from "../../../../../images/icons/circle-plus.svg";
import TopArrow from "../../../../../images/icons/top-small-arrow.svg";
import DownArrow from "../../../../../images/icons/bottom-small-arrow.svg";

/**
 * Creates project dropdown helper functions
 * @param {Object} dependencies - Object containing all necessary dependencies
 */
export const createProjectDropdownHelpers = ({
  // State values
  projectName,
  sprintName,
  client,
  letterCount,
  projectStatusFilter,
  isProjectStatusDropdownOpen,
  projectDropdownOpen,
  filter,
  selectedClients,
  selectedProject,
  filteredClientData,
  tag,
  tagDropdownOpen,
  filteredTagItems,
  isBillable,
  user,
  // Refs
  trackerOuterRef2,
  projectStatusDropdownRef,
  tagDropdownRef,
  // Setters
  setProjectDropdownOpen,
  setProjectStatusFilter,
  setFilter,
  setSelectedClients,
  setSelectedProject,
  setTaskError,
  setTagDropdownOpen,
  setProjectName,
  setProjectId,
  setClient,
  setClientId,
  setIsBillable,
  setSprintId,
  setSprintName,
  setTag,
  setTagId,
  // Handlers
  toggleProjectStatusDropdown,
  handleProjectStatusChange,
  handleTagInputChange,
}) => {
  /**
   * Handles project dropdown toggle
   */
  const handleProjectDropdownToggle = () => {
    setProjectDropdownOpen(!projectDropdownOpen);
    setProjectStatusFilter("Active");
    setFilter("");
    setSelectedClients(new Set());
    setSelectedProject(null);
    setTaskError(false);
    setTagDropdownOpen(false);
  };

  /**
   * Renders project name display
   */
  const renderProjectNameDisplay = (showTooltip = true, maxWidth = "294.91px") => {
    return (
      <>
        {projectName && showTooltip && (
          <span className={`tooltip-tracker ${letterCount < 34 ? "small" : "large"}`}>
            {projectName} {sprintName ? ` - ${sprintName}` : ""}
            {projectName && (
              <div className="project-name-tracker">
                {client}
              </div>
            )}
          </span>
        )}
        <div
          className={`tracker-top-outer-project ${letterCount} ${letterCount < 34 ? "small" : "large"}`}
          ref={trackerOuterRef2}
          style={{
            ...commonTextStyle,
            maxWidth,
          }}
        >
          {!projectName && (
            <>
              {" "}
              <img src={Plus} alt="plus" />{" "}
            </>
          )}
          {projectName ? projectName : "Sprint/Project"}
          {sprintName ? ` - ${sprintName}` : ""}
          {projectName && (
            <div className="project-name-tracker">{client}</div>
          )}
        </div>
      </>
    );
  };

  /**
   * Renders "No sprints/projects" messages
   */
  const renderNoItemsMessage = (message, isSprint = false) => {
    const style = isSprint
      ? {
        pointerEvents: "none",
        cursor: "default",
        opacity: 0.6,
        fontStyle: "italic",
        color: "#999",
        paddingLeft: "20px",
      }
      : {};
    return <div style={style}>{message}</div>;
  };

  /**
   * Renders project status filter dropdown
   */
  const renderProjectStatusFilter = () => {
    if (user.userType !== userTypeConsts.SUPER_ADMIN && user.userType !== userTypeConsts.ADMIN) {
      return null;
    }
    return (
      <>
        {/* IMPORTANT: the status list must be INSIDE this ref container.
            We attach a document-level mousedown (capture) handler for click-outside,
            so if the <ul> is outside the ref, clicks get treated as "outside" and
            the dropdown closes before the option click fires. */}
        <div
          className="user-status-dropdown"
          ref={projectStatusDropdownRef}
          // Ensure the status list can be absolutely positioned without affecting layout
          style={{ position: "relative" }}
        >
          <div className="user-status-dropdown-label" htmlFor="projectStatusFilter">Filter by Status</div>
          <div className="custom-dropdown-user">
            <div className="showing-name" onClick={toggleProjectStatusDropdown}>
              {projectStatusFilter ? projectStatusFilter : "Active"}
            </div>
          </div>
          {isProjectStatusDropdownOpen && (
            <ul
              className="status-list"
              // Tracker.css has absolute positioning commented out; keep layout stable here.
              style={{ position: "absolute", top: "52px", left: 0, width: "100%", zIndex: 99 }}
            >
              <li
                onClick={() => {
                  handleProjectStatusChange("All");
                  toggleProjectStatusDropdown();
                }}
                className={projectStatusFilter === "All" ? "selected-item" : ""}
              >
                All
              </li>
              <li
                onClick={() => {
                  handleProjectStatusChange("Active");
                  toggleProjectStatusDropdown();
                }}
                className={projectStatusFilter === "Active" ? "selected-item" : ""}
              >
                Active
              </li>
              <li
                onClick={() => {
                  handleProjectStatusChange("Inactive");
                  toggleProjectStatusDropdown();
                }}
                className={projectStatusFilter === "Inactive" ? "selected-item" : ""}
              >
                Inactive
              </li>
            </ul>
          )}
        </div>
      </>
    );
  };

  /**
   * Renders tag icon or name
   */
  const renderTagIconOrName = (tagValue) => {
    return tagValue ? (
      <span className="tooltip-tracker">{tagValue}</span>
    ) : (
      <TagIconSVG />
    );
  };

  /**
   * Handles sprint selection
   */
  const handleSprintSelect = (project, sprint, clientObj, shouldCloseDropdown = true) => {
    setProjectName(project.project_name);
    setProjectId(project._id);
    setClient(clientObj.client_name && clientObj.client_name);
    setClientId(clientObj._id && clientObj._id);
    setIsBillable(project.is_billable);
    setSprintId(sprint._id);
    setSprintName(sprint.sprint_name);
    if (shouldCloseDropdown) {
      setProjectDropdownOpen(false);
    }
  };

  /**
   * Checks if project matches filter
   */
  const projectMatchesFilter = (project, filterText, statusFilter) => {
    // Exclude archived projects
    if (project.is_archive === true) {
      return false;
    }

    const nameMatches = project.project_name
      ?.toLowerCase()
      .includes(filterText.toLowerCase());
    const statusMatches =
      statusFilter === "All"
        ? true
        : statusFilter === "Active"
          ? project.is_active === true
          : project.is_active === false;
    return nameMatches && statusMatches;
  };

  /**
   * Handles project filter input change
   */
  const handleProjectFilterChange = (filterValue, includeStatusFilter = true) => {
    setFilter(filterValue);
    if (filterValue === "") {
      setSelectedClients(new Set());
    } else {
      const matchingClients = filteredClientData.filter((clientItem) =>
        clientItem.projects.some((project) => {
          const nameMatches = project.project_name
            ?.toLowerCase()
            .includes(filterValue.toLowerCase());
          const statusMatches = includeStatusFilter
            ? (projectStatusFilter === "All"
              ? true
              : projectStatusFilter === "Active"
                ? project.is_active === true
                : project.is_active === false)
            : true;
          return nameMatches && statusMatches;
        })
      );
      setSelectedClients(new Set(matchingClients));
    }
  };

  /**
   * Renders inline tag dropdown (for responsive layouts)
   */
  const renderInlineTagDropdown = (maxWidth = "150px") => {
    return (
      <div className="tag-section-outer" ref={tagDropdownRef}>
        <div
          style={pointerCursorStyle}
          className="tag-section"
          onClick={() => {
            setTagDropdownOpen(!tagDropdownOpen);
            setProjectDropdownOpen(false);
          }}
        >
          {renderTagIconOrName(tag)}
          {tag && (
            <p
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth,
              }}
            >
              {tag}
            </p>
          )}
        </div>
        {tagDropdownOpen && (
          <div className="custom-dropdown">
            <input
              type="text"
              className="search-bar"
              placeholder="Search..."
              value={tag}
              onChange={handleTagInputChange}
              autoComplete="off"
            />
            <ul className="client-list">
              {filteredTagItems.map((item) => (
                <li
                  className="cursor"
                  key={item._id}
                  onClick={(e) => {
                    setTag(item.tag_name);
                    setTagId(item._id);
                    setTagDropdownOpen(false);
                  }}
                >
                  {item.tag_name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  /**
   * Renders billable icon
   */
  const renderBillableIcon = () => {
    return (
      <div className="billable-section">
        {isBillable ? (
          <div className="billable-timing-sec billable-timing">
            <svg
              width="13"
              height="20"
              viewBox="0 0 13 20"
              fill="#405BFF"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.544 19.32V17.4C3.264 17.184 1.464 16.2 0.024 14.688L1.872 12.792C3.288 14.232 4.848 14.952 6.648 14.904C8.304 14.88 9.408 14.232 9.408 13.176C9.408 11.808 8.064 11.664 5.616 11.04C3.336 10.464 0.384 9.72 0.384 6.792C0.384 4.32 2.736 2.568 5.592 2.376V0.503999H7.248V2.424C9.024 2.64 10.776 3.408 12.192 4.848L10.368 6.744C8.952 5.448 7.704 4.8 6.072 4.8C4.32 4.8 3.168 5.424 3.168 6.48C3.168 7.656 4.464 8.064 6.936 8.592C9.24 9.12 12.384 9.768 12.384 12.888C12.384 15.6 10.08 17.184 7.2 17.4V19.32H5.544Z"
                fill="#96A8BA"
              />
            </svg>
          </div>
        ) : (
          <div className="billable-timing-sec non-billable-timing">
            <svg
              width="13"
              height="20"
              viewBox="0 0 13 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.544 19.32V17.4C3.264 17.184 1.464 16.2 0.024 14.688L1.872 12.792C3.288 14.232 4.848 14.952 6.648 14.904C8.304 14.88 9.408 14.232 9.408 13.176C9.408 11.808 8.064 11.664 5.616 11.04C3.336 10.464 0.384 9.72 0.384 6.792C0.384 4.32 2.736 2.568 5.592 2.376V0.503999H7.248V2.424C9.024 2.64 10.776 3.408 12.192 4.848L10.368 6.744C8.952 5.448 7.704 4.8 6.072 4.8C4.32 4.8 3.168 5.424 3.168 6.48C3.168 7.656 4.464 8.064 6.936 8.592C9.24 9.12 12.384 9.768 12.384 12.888C12.384 15.6 10.08 17.184 7.2 17.4V19.32H5.544Z"
                fill="#96A8BA"
              />
            </svg>
          </div>
        )}
      </div>
    );
  };

  /**
   * Renders project name display for small screens
   */
  const renderProjectNameDisplaySmall = (maxWidth = "150px") => {
    return (
      <>
        {!projectName && (
          <>
            <img src={Plus} alt="plus" />{" "}
          </>
        )}
        {projectName && (
          <span className={`tooltip-tracker ${letterCount < 34 ? "small" : "large"}`}>
            {projectName ? projectName : "Sprint/Project"}
            {sprintName ? ` - ${sprintName}` : ""}
            {projectName && (
              <div className="project-name-tracker">
                . {client}
              </div>
            )}
          </span>
        )}
        <span
          className="tracker-project-tag-project"
          ref={trackerOuterRef2}
          style={{
            ...commonTextStyle,
            maxWidth,
          }}
        >
          {projectName ? projectName : "Sprint/Project"}
          {sprintName ? ` - ${sprintName}` : ""}
          {projectName && (
            <div className="project-name-tracker">
              . {client}
            </div>
          )}
        </span>
      </>
    );
  };

  /**
   * Renders project dropdown with filter
   */
  const renderProjectDropdownWithFilter = (listClassName = "", includeStatusFilter = true) => {
    return (
      <>
        {renderProjectStatusFilter()}
        <div className="top-search-tracker-for">
          <input
            type="text"
            className="search-bar"
            placeholder="Find Project or Client"
            value={filter}
            onChange={(e) => handleProjectFilterChange(e.target.value, includeStatusFilter)}
            autoComplete="off"
          />
        </div>
        {renderProjectDropdownContent(listClassName, true)}
      </>
    );
  };

  /**
   * Renders project dropdown content
   */
  const renderProjectDropdownContent = (listClassName = "", shouldCloseOnSprintSelect = true) => {
    return (
      <ul className={`tracker-who-list client-list client-list-filter ${listClassName}`}>
        {filteredClientData.map((clientItem) => {
          // `filteredClientData` preserves original client references (for selectedClients Set),
          // so we filter the projects at render-time.
          const filteredProjects = (clientItem.projects || []).filter((project) =>
            projectMatchesFilter(project, filter, projectStatusFilter)
          );

          return (
            <div key={clientItem._id} className="main-outer-item-dd">
              <div
                className={`client-name1 cursor ${selectedClients.has(clientItem) ? "selected" : ""}`}
                onClick={() => {
                  if (filteredProjects.length > 0) {
                    const newSelectedClients = new Set(selectedClients);
                    if (selectedClients.has(clientItem)) {
                      newSelectedClients.delete(clientItem);
                    } else {
                      newSelectedClients.add(clientItem);
                    }
                    setSelectedClients(newSelectedClients);
                  }
                }}
              >
                <span className="main-project">{clientItem.client_name} </span>
                {filteredProjects.length > 0 && (
                  <span className={`arrow ${selectedClients.has(clientItem) ? "open" : ""}`}>
                    <span className="main-project-title">
                      {filteredProjects.length} Projects
                    </span>
                    {selectedClients.has(clientItem) ? (
                      <img src={TopArrow} alt="Open" />
                    ) : (
                      <img src={DownArrow} alt="Close" />
                    )}
                  </span>
                )}
              </div>
              {selectedClients.has(clientItem) && (
                <div className="project-name1-outer">
                  {filteredProjects.length === 0 ? (
                    renderNoItemsMessage("No projects under this client")
                  ) : (
                    filteredProjects
                      .sort((a, b) => a.project_name.localeCompare(b.project_name))
                      .map((project) => (
                        <div key={project._id}>
                          <div className={`project-name1 ${selectedProject === project ? "selected" : ""}`}>
                            <div
                              onClick={() =>
                                setSelectedProject(selectedProject === project ? null : project)
                              }
                              className="project-dd-title"
                            >
                              {project.project_name}{" "}
                            </div>
                            <div className="cursor">
                              {project.sprints.length > 0 && (
                                <span
                                  className={`arrow ${selectedProject === project ? "open" : ""}`}
                                  onClick={() =>
                                    setSelectedProject(selectedProject === project ? null : project)
                                  }
                                >
                                  <span className="main-project-title">
                                    {project.sprints.length} Sprints
                                  </span>
                                  {selectedProject === project ? (
                                    <img src={TopArrow} alt="Open" />
                                  ) : (
                                    <img src={DownArrow} alt="Close" />
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                          {selectedProject === project && (
                            <div>
                              {project.sprints.length === 0 ? (
                                renderNoItemsMessage("No sprints under this project", true)
                              ) : (
                                <ul className="sprint-list">
                                  {project.sprints
                                    .sort((a, b) => a.sprint_name.localeCompare(b.sprint_name))
                                    .map((sprint) => (
                                      <li
                                        key={sprint._id}
                                        onClick={(e) => {
                                          handleSprintSelect(project, sprint, clientItem, shouldCloseOnSprintSelect);
                                        }}
                                      >
                                        {sprint.sprint_name}
                                      </li>
                                    ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </ul>
    );
  };

  return {
    handleProjectDropdownToggle,
    renderProjectNameDisplay,
    renderNoItemsMessage,
    renderProjectStatusFilter,
    renderTagIconOrName,
    handleSprintSelect,
    projectMatchesFilter,
    handleProjectFilterChange,
    renderInlineTagDropdown,
    renderBillableIcon,
    renderProjectNameDisplaySmall,
    renderProjectDropdownWithFilter,
    renderProjectDropdownContent,
  };
};
